import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import moment from 'moment';
import { AppContext } from 'providers/AppProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWeight } from '@fortawesome/free-solid-svg-icons';

const VITAL_CONFIG = {
  'Blood Pressure':    { icon: '🫀', fa: null },
  'Temperature':       { icon: '🌡️', fa: null },
  'Pulse':             { icon: '💓', fa: null },
  'Weight':            { icon: null, fa: faWeight },
  'Respiration':       { icon: '🌬️', fa: null },
  'Blood Sugar':       { icon: '🩸', fa: null },
  'Oxygen Saturation': { icon: '🫁', fa: null },
  'Pain':              { icon: '🤕', fa: null },
};

const getStyles = (isDark) => ({
  modalBody:   { background: isDark ? 'transparent' : '#f8fafc', padding: '20px 24px' },
  modalHeader: { borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}` },
  modalFooter: { borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`, background: isDark ? 'transparent' : '#fff' },
  title:       { fontSize: 17, fontWeight: 600, letterSpacing: -0.3, color: isDark ? '#f0f1f5' : '#111827' },
  subtitle:    { fontSize: 12, color: isDark ? '#6b7280' : '#9ca3af', fontFamily: 'monospace', marginTop: 3 },
  badge: {
    background: isDark ? 'rgba(99,102,241,0.12)' : '#ede9fe',
    border: `1px solid ${isDark ? 'rgba(99,102,241,0.25)' : '#c4b5fd'}`,
    color: isDark ? '#818cf8' : '#7c3aed',
    fontSize: 11, fontFamily: 'monospace', padding: '5px 10px',
    borderRadius: 6, whiteSpace: 'nowrap', letterSpacing: 0.3,
    fontWeight: 500, marginRight: 8,
  },
  dateLabel: {
    fontSize: 10.5, color: isDark ? '#374151' : '#9ca3af',
    fontFamily: 'monospace', textTransform: 'uppercase',
    letterSpacing: 1, marginBottom: 12,
  },
  footerMeta:      { fontSize: 12, color: isDark ? '#4b5563' : '#9ca3af', fontFamily: 'monospace', flex: 1 },
  footerMetaSpan:  { color: isDark ? '#6b7280' : '#374151', fontWeight: 500 },
});

const VitalCard = ({ vital, isDark }) => {
  const isOut = vital.is_out_of_range === 1;
  const config = VITAL_CONFIG[vital.name] || { icon: '📋' };
//   const displayValue = parseFloat(vital.value_1).toFixed(
//     Number.isInteger(parseFloat(vital.value_1)) ? 0 : 1
//   );
// Replace the displayValue logic with this:
const getDisplayValue = (vital) => {
  if (vital.data_type === 'text') {
    return vital.text_value ?? '-';
  }
  if (vital.data_type === 'dual') {
    const v1 = parseFloat(vital.value_1).toFixed(0);
    const v2 = parseFloat(vital.value_2).toFixed(0);
    return `${v1}/${v2}`;
  }
  // single
  const num = parseFloat(vital.value_1);
  return num.toFixed(Number.isInteger(num) ? 0 : 1);
};

  const cardStyle = {
    borderRadius: 13, padding: '16px 18px',
    display: 'flex', alignItems: 'center', gap: 14,
    position: 'relative', overflow: 'hidden',
    marginBottom: 10,
    ...(isDark ? {
      background: isOut
        ? 'linear-gradient(135deg, #1a1d2a 60%, rgba(239,68,68,0.05))'
        : '#1a1d2a',
      border: `1px solid ${isOut ? 'rgba(239,68,68,0.25)' : 'rgba(20,184,166,0.2)'}`,
    } : {
      background: isOut
        ? 'linear-gradient(135deg, #fff 60%, #fff5f5)'
        : 'linear-gradient(135deg, #fff 60%, #f0fdf4)',
      border: `1px solid ${isOut ? '#fecaca' : '#d1fae5'}`,
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }),
  };

  const accentColor = isOut
    ? '#ef4444'
    : isDark ? '#14b8a6' : '#10b981';

  const iconBg = isDark
    ? isOut ? 'rgba(239,68,68,0.1)' : 'rgba(20,184,166,0.1)'
    : isOut ? '#fef2f2' : '#ecfdf5';

  const pillStyle = isDark
    ? isOut
      ? { background: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.2)' }
      : { background: 'rgba(20,184,166,0.12)', color: '#2dd4bf', border: '1px solid rgba(20,184,166,0.2)' }
    : isOut
      ? { background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' }
      : { background: '#d1fae5', color: '#065f46', border: '1px solid #a7f3d0' };

  const valueColor = isDark
    ? isOut ? '#f87171' : '#5eead4'
    : isOut ? '#dc2626' : '#059669';

  return (
    <div style={cardStyle}>
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 4,
        background: accentColor, borderRadius: '2px 0 0 2px',
      }} />

<div style={{
  width: 44, height: 44, borderRadius: 11, flexShrink: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: config.fa ? 22 : 20,
  background: iconBg,
}}>
  {config.fa
    ? <FontAwesomeIcon icon={config.fa} style={{ color: isOut ? '#ef4444' : '#14b8a6', fontSize: 22 }} />
    : config.icon
  }
</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: isDark ? '#d1d5db' : '#111827', marginBottom: 2 }}>
          {vital.name}
        </div>
        <div style={{ fontSize: 11.5, color: isDark ? '#4b5563' : '#9ca3af', fontFamily: 'monospace' }}>
          {vital.short_name} · Single reading
        </div>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 10.5, fontWeight: 500, padding: '3px 9px',
          borderRadius: 20, marginTop: 6, ...pillStyle,
        }}>
          {isOut ? '⚠ Out of Range' : '✓ Normal'}
        </span>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontFamily: 'monospace', fontSize: 26, fontWeight: 600, lineHeight: 1, marginBottom: 3, color: valueColor }}>
          {getDisplayValue(vital)}
        </div>
        <div style={{ fontSize: 11, color: isDark ? '#6b7280' : '#9ca3af', fontFamily: 'monospace', letterSpacing: 0.5 }}>
          {vital.unit}
        </div>
      </div>
    </div>
  );
};

const VitalSignModalMedpass = ({ vitals, show, onHide }) => {
  const { config: { isDark } } = React.useContext(AppContext); // 👈 Falcon theme detection
  const s = getStyles(isDark);

  const hasData = Array.isArray(vitals) && vitals.length > 0;
  const { patient_id, prescription_id, pharmacy_order_id, medpass_id, recorded_by, reading_datetime } =
    hasData ? vitals[0] : {};

  return (
    <Modal show={show} onHide={onHide} centered size="md">
      <Modal.Header closeButton style={s.modalHeader}>
        <div className='me-5'>
          <Modal.Title style={s.title}>Vital Signs · Medpass</Modal.Title>
          {hasData && <div style={s.subtitle}>Patient #{patient_id} · Rx #{prescription_id} · Order #{pharmacy_order_id}</div>}
        </div>
        {hasData && <span style={s.badge}>Medpass #{medpass_id}</span>}
      </Modal.Header>

      <Modal.Body style={s.modalBody}>
        {!hasData ? (
          <p style={{ color: '#9ca3af', textAlign: 'center', margin: 0 }}>No vitals data available.</p>
        ) : (
          <>
            <div style={s.dateLabel}>
              Recorded · {moment(reading_datetime).format('MMM DD, YYYY · hh:mm A')}
            </div>
            {vitals.map(vital => (
              <VitalCard key={vital.id} vital={vital} isDark={isDark} />
            ))}
          </>
        )}
      </Modal.Body>

      <Modal.Footer style={s.modalFooter}>
        {hasData && (
          <span style={s.footerMeta}>
            Recorded by <span style={s.footerMetaSpan}>User #{recorded_by}</span>
          </span>
        )}
        <Button variant="secondary" size="sm" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VitalSignModalMedpass;